import { CLEANING_FREQUENCY_ORDER, CLEANING_FREQUENCIES, meetsCleaningMinimum } from '../property/settings.js'

/**
 * Can this home go on the public Listing site, and if not, what exactly is missing?
 *
 * THE RULE IS THE PRODUCT OWNER'S, 2026-09-25:
 *
 *   "把那个清洁的选项也加在这个上面…把价格都列在上面。所以我们 Property 上面的 Setup 就一定要有这些东西。"
 *
 * A listing shows a price, and a price shown to a stranger is a promise. So a home is listable
 * only when every figure the page prints came from a person, not from a seed: a monthly rent
 * somebody agreed, and a price for every cleaning frequency the renter can pick.
 *
 * "EVERY FREQUENCY" MEANS EVERY ONE THE OWNER ALLOWS. An owner who requires at least monthly
 * cleaning has ruled out "once, at checkout". That frequency is not offered, so it is not missing;
 * listing it as a gap would send somebody to fill in a price nobody may ever charge.
 *
 * ABSENCE IS NEVER READINESS. A home with no Property setup at all is not "ready with defaults" —
 * it is not ready, and the list says so in words.
 */

export const MIN_PHOTOS = 5

const LABEL = Object.fromEntries(CLEANING_FREQUENCIES.map((f) => [f.id, f.label]))

/**
 * `calendar` is `calendarCheck(...)`'s answer. Left out, the calendar is not judged here at all —
 * which callers that price a quote rely on. The Listing always passes it, and there a home is
 * listable only when its calendar is 'ok' (#57).
 */
export function readinessOf(unit, settings, { calendar, status = null } = {}) {
  const missing = []

  /*
    TAKEN OFF HERE BY A PERSON (#199): Unlisted, Deactivated or Deleted in Property setup. Not on the
    Listing and not a page a renter can open. `status` is the home's state from
    property/homeStatus.js; absent, the two older rules below decide exactly as they always did.
  */
  if (status && status !== 'LISTED' && status !== 'OFF_IN_GUESTY') {
    const says = { UNLISTED: 'Unlisted in Property setup.', DEACTIVATED: 'Deactivated in Property setup.',
      DELETED: 'Deleted — in Archive in Property setup.' }[status] ?? 'Taken off in Property setup.'
    return { listable: false, midTerm: false, offeredCleaning: [], missing: [{ id: `HOME_${status}`, says }] }
  }

  /* Deactivated in Guesty: it cannot be rented, Guesty sends it no calendar, and it is not on the
     Listing at all. The Product Owner's decision, 2026-09-25 (#57). */
  if (unit?.active === false) {
    return { listable: false, midTerm: false, inactive: true, offeredCleaning: [], missing: [
      { id: 'INACTIVE_IN_GUESTY', says: 'Deactivated in Guesty.' },
    ] }
  }

  if (settings?.offeredForMidTerm === false && status !== 'LISTED') {
    return { listable: false, midTerm: false, offeredCleaning: [], missing: [
      { id: 'NOT_MID_TERM', says: 'Not offered for mid-term in Property setup.' },
    ] }
  }

  if (!settings || settings.configuredAt == null) {
    missing.push({ id: 'NOT_SET_UP', says: 'Property setup has never been saved for this home.' })
  }
  if (settings?.monthlyRent == null) {
    missing.push({ id: 'NO_RENT', says: 'No monthly rent.' })
  }
  if (settings?.floor == null) {
    missing.push({ id: 'NO_FLOOR', says: 'No minimum rent (floor).' })
  }

  const offeredCleaning = []
  for (const f of CLEANING_FREQUENCY_ORDER) {
    if (!meetsCleaningMinimum(f, settings?.cleaningOwnerMinimum)) continue
    /* A frequency the company does not offer (Pricing › Cleaning) is not missing — it is not on sale. */
    if (Array.isArray(settings?.cleaningOffered) && !settings.cleaningOffered.includes(f)) continue
    /* A $0 cleaning is a waived fee, which the quote engine refuses (P-05) — so it is missing, not offered. */
    const price = settings?.cleaningPrices?.[f]
    if (!(Number(price) > 0)) {
      missing.push({ id: `NO_CLEAN_${f}`, says: `No cleaning price for “${LABEL[f] ?? f}”.` })
    } else {
      offeredCleaning.push(f)
    }
  }

  /*
    COUNT THE PICTURES WHEN THEY ARE HERE; ASK WHEN THEY ARE NOT (#402).

    The staff app reads `v_property_brief`, which leaves out 205 homes' 9,946 photograph urls —
    1,981 kB that nothing on a staff screen draws — and carries `photoCount` instead, counted by
    the view from the array it still had. So a trimmed row answers from that, and a row that has
    its photographs counts them.

    NOT ONE RULE FOR BOTH, on purpose. `content.photoCount` is the vendor's own figure and it is
    not always right: on production 2026-10-06, one home of 205 claimed 104 and had 100. Where the
    pictures are in hand, they are the better answer, and this is the rule that decides whether a
    home may be listed at all.
  */
  const photos = unit?.listingTrimmed ? (unit.photoCount ?? 0) : (unit?.photos?.length ?? 0)
  if (photos < MIN_PHOTOS) {
    missing.push({ id: 'FEW_PHOTOS', says: `${photos} photo${photos === 1 ? '' : 's'} — a listing needs at least ${MIN_PHOTOS}.` })
  }

  /*
    THE CALENDAR FAULT IS SAID IN WORDS, AND IT IS SAID AS A FAULT (#57). A person reading the
    list must be able to tell "somebody has not filled in a price" from "our sync is broken".
  */
  if (calendar?.state === 'gaps') {
    const n = calendar.missingDays
    missing.push({ id: 'CALENDAR_GAP', fault: true,
      says: `Guesty sent no calendar for ${n} day${n === 1 ? '' : 's'} (from ${calendar.firstMissing}). This is a sync fault and needs fixing.` })
  } else if (calendar?.state === 'error') {
    missing.push({ id: 'CALENDAR_ERROR', says: 'The calendar could not be loaded, so this home cannot be checked.' })
  }
  const calendarOk = calendar === undefined || calendar.state === 'ok'

  return { listable: missing.length === 0 && calendarOk, midTerm: true, offeredCleaning, missing,
    calendarState: calendar?.state ?? null }
}

/**
 * The missing list as one readable line. Four missing cleaning prices are one fact to a person —
 * "no cleaning prices for these" — not four sentences in a row.
 */
export function missingSentence(missing = []) {
  const cleaning = missing.filter((m) => m.id.startsWith('NO_CLEAN_')).map((m) => (LABEL[m.id.slice(9)] ?? m.id.slice(9)).toLowerCase())
  /* A sync fault is said first: it is ours to fix, and it outranks a price somebody has not typed. */
  const rest = [...missing.filter((m) => m.fault), ...missing.filter((m) => !m.fault)]
    .filter((m) => !m.id.startsWith('NO_CLEAN_')).map((m) => m.says)
  if (cleaning.length) rest.push(`No cleaning price for ${cleaning.join(', ')}.`)
  return rest.join(' ')
}
